import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getUserExpenseSummary } from '../../../services/groupService';
import { useAuth } from '../../../contexts/AuthContext';

const CostSummary = ({ totalCost, breakdown, travelers, groupId, onViewSplits }) => {
  const { user } = useAuth();
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const costPerPerson = totalCost / travelers;

  const categories = [
    { key: 'activities', label: 'Activities & Tours', icon: 'Waves', color: 'text-teal-600' },
    { key: 'accommodation', label: 'Accommodation', icon: 'Hotel', color: 'text-purple-600' },
    { key: 'food', label: 'Food & Dining', icon: 'Utensils', color: 'text-orange-600' },
    { key: 'transport', label: 'Transportation', icon: 'Car', color: 'text-blue-600' }
  ];

  const loadExpenseSummary = async () => {
    if (!groupId || !user?.id) return;
    
    setIsLoading(true);
    try {
      const summary = await getUserExpenseSummary(groupId, user?.id);
      setExpenseSummary(summary);
    } catch (error) {
      console.error('Error loading expense summary:', error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (groupId && user?.id) {
      loadExpenseSummary();
    }
  }, [groupId, user?.id]);

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-lg">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-secondary p-6 text-white">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Wallet" size={24} />
          <h3 className="text-xl font-bold">Trip Budget</h3>
        </div>
        <div className="mt-4">
          <p className="text-sm opacity-90 mb-1">Total Cost</p>
          <p className="text-4xl font-bold">₱{totalCost?.toLocaleString()}</p>
        </div>
        <div className="mt-4 pt-4 border-t border-white/20">
          <p className="text-sm opacity-90 mb-1">Per Person ({travelers} travelers)</p>
          <p className="text-2xl font-semibold">₱{costPerPerson?.toLocaleString()}</p>
        </div>
      </div>

      {/* Group Expense Summary */}
      {groupId && expenseSummary && (
        <div className="p-6 bg-accent/10 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-foreground flex items-center gap-2">
              <Icon name="Users" size={16} className="text-accent" />
              Your Share
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={onViewSplits}
              iconName="ExternalLink"
              iconPosition="right"
            >
              View Details
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Total Owed</p>
              <p className="text-lg font-bold text-destructive">
                ₱{expenseSummary?.totalOwed?.toLocaleString()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Paid</p>
              <p className="text-lg font-bold text-success">
                ₱{expenseSummary?.totalPaid?.toLocaleString()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Total</p>
              <p className="text-lg font-bold text-foreground">
                ₱{expenseSummary?.totalExpenses?.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Breakdown */}
      <div className="p-6 space-y-4">
        <h4 className="font-semibold text-foreground mb-4">Cost Breakdown</h4>
        {categories?.map((category) => {
          const amount = breakdown?.[category?.key] || 0;
          const percentage = totalCost > 0 ? (amount / totalCost) * 100 : 0;
          
          return (
            <div key={category?.key} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name={category?.icon} size={16} className={category?.color} />
                  <span className="text-sm text-foreground">{category?.label}</span>
                </div>
                <span className="text-sm font-semibold text-foreground">
                  ₱{amount?.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-right">
                {percentage?.toFixed(1)}% of total
              </p>
            </div>
          );
        })}
      </div>

      {/* Tips */}
      <div className="p-6 bg-accent/10 border-t border-border">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
            <Icon name="Lightbulb" size={16} className="text-accent" />
          </div>
          <div>
            <h5 className="font-semibold text-foreground text-sm mb-1">Budget Tip</h5>
            <p className="text-xs text-muted-foreground">
              {groupId 
                ? 'Track shared expenses and settle up with your group members easily.' :'Consider setting aside an additional 10-15% for unexpected expenses and souvenirs.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostSummary;